import { Suspense } from 'react';
import { Accordion } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { HelpUsageGuide } from '../../config/helpPage.config';
import { gaisukePublicPath } from '../../utils/gaisukePath';
import { laboSukePublicPath } from '../../utils/laboSukePath';
import { lazyWithChunkReload } from '../../utils/lazyWithChunkReload';

function resolveHelpGuidePath(guide: HelpUsageGuide, locale: string): string {
  if (guide.id === 'labo_suke') return laboSukePublicPath(locale);
  if (guide.id === 'gaisuke') return gaisukePublicPath(locale);
  return guide.path;
}

const HelpReservationGuideContent = lazyWithChunkReload(() =>
  import('./HelpReservationGuide').then((m) => ({ default: m.HelpReservationGuideContent })),
);

interface HelpUsageGuideItemProps {
  guide: HelpUsageGuide;
  eventKey: string;
  isExpanded: boolean;
}

export default function HelpUsageGuideItem({ guide, eventKey, isExpanded }: HelpUsageGuideItemProps) {
  const { t, i18n } = useTranslation();

  return (
    <Accordion.Item eventKey={eventKey}>
      <Accordion.Header>{t(guide.titleKey)}</Accordion.Header>
      <Accordion.Body>
        {!isExpanded ? null : guide.kind === 'reservation' ? (
          <Suspense
            fallback={<span className="text-muted small">{t('common.processing')}</span>}
          >
            <HelpReservationGuideContent />
          </Suspense>
        ) : (
          <>
            {guide.descKey && <p className="text-muted">{t(guide.descKey)}</p>}

            {guide.kind === 'steps' && guide.stepsKey && (
              <ol className="mb-3">
                {guide.stepsKey.map((stepKey) => (
                  <li key={stepKey}>{t(stepKey)}</li>
                ))}
              </ol>
            )}

            {(guide.kind === 'gene' ||
              guide.kind === 'gaisuke' ||
              guide.kind === 'transit' ||
              guide.kind === 'access_logs') &&
              guide.extraKeys && (
                <ul className="mb-3">
                  {guide.extraKeys.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              )}

            {guide.noteKey && (
              <p className="small text-warning mb-3">
                <i className="fas fa-exclamation-triangle me-1" />
                {t(guide.noteKey)}
              </p>
            )}

            <Link
              to={resolveHelpGuidePath(guide, i18n.language)}
              className="btn btn-outline-primary btn-sm"
            >
              <i className="fas fa-external-link-alt me-1" />
              {t('help.open_feature')}
            </Link>
          </>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
}
